'use client';

import { useState, useEffect, useRef } from 'react';
import VehicleInputForm from './VehicleInputForm';
import AIRecommendations from './AIRecommendations';
import SafetyWarnings from './SafetyWarnings';
import PartsResultCard from './PartsResultCard';
import FilterOptions from './FilterOptions';
import RelatedParts from './RelatedParts';
import Icon from '@/components/ui/AppIcon';
import { ApiResponse, CostAnalysisResponse } from '@/lib/types/openai';

interface VehicleDetails {
  year: string;
  make: string;
  model: string;
  mileage: string;
  partDescription: string;
}

interface Recommendation {
  partNumber: string;
  partName: string;
  keywords: string[];
  compatibility: string;
  estimatedPrice: string;
}

interface PartResult {
  id: string;
  title: string;
  partNumber: string;
  price: string;
  condition: string;
  seller: {
    name: string;
    rating: number;
    feedbackCount: number;
    topRated: boolean;
  };
  shipping: string;
  imageUrl: string;
  imageAlt: string;
  compatibility: string;
  listingUrl: string;
}

interface FilterState {
  priceRange: [number, number];
  condition: string[];
  sellerRating: number;
  shipping: string[];
}

const OEMPartsFinderInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [vehicleDetails, setVehicleDetails] = useState<VehicleDetails | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [partsResults, setPartsResults] = useState<PartResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<PartResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  // Debounce timer to prevent rapid requests
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSearchRef = useRef<number>(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Check for direct search queries when landing from the DIY page
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const query = params.get('q');
      if (query && !hasSearched && !isSearching) {
        setIsSearching(true);
        
        // Populate vehicle details mock so the Amazon affiliate fallback has context
        setVehicleDetails({
          year: '',
          make: '',
          model: '',
          mileage: '',
          partDescription: query
        });

        // Directly hit the eBay API and bypass Gemini since we don't have full vehicle context
        const runDirectFetch = async () => {
          await fetchEbayResults(query);
          setIsSearching(false);
          setHasSearched(true);
        };
        runDirectFetch();
      }
    }
  }, []);

  const handleSearch = async (details: VehicleDetails, isRetry: boolean = false) => {
    // Prevent duplicate requests within 2 seconds
    const now = Date.now();
    if (!isRetry && now - lastSearchRef.current < 2000) {
      console.log('Request throttled - please wait before searching again');
      return;
    }
    lastSearchRef.current = now;

    // Clear any pending searches
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setIsSearching(true);
    setVehicleDetails(details);
    setError(null);

    if (!isRetry) {
      setRetryCount(0);
    }

    try {
      // Call Gemini cost analysis API
      const res = await fetch('/api/gemini/cost-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vehicleYear: details.year,
          vehicleMake: details.make,
          vehicleModel: details.model,
          partDescription: details.partDescription,
          mileage: details.mileage,
        }),
      });

      const data: ApiResponse<CostAnalysisResponse> = await res.json();

      if (data.success) {
        // Transform AI recommendations to component format
        const aiRecommendations = data.data.recommendations.map(rec => ({
          partNumber: rec.partNumber,
          partName: rec.partName,
          keywords: rec.keywords,
          compatibility: rec.compatibility,
          estimatedPrice: rec.estimatedPrice,
        }));

        setRecommendations(aiRecommendations);

        // Fetch real marketplace results from eBay
        // Try with a slightly looser query first to ensure we get results
        const searchQuery = `${details.make} ${details.model} ${details.partDescription} OEM`;
        await fetchEbayResults(searchQuery, aiRecommendations[0]);

        setHasSearched(true);
        setRetryCount(0);
        setIsRetrying(false);
      } else {
        const errorMessage = data.error.message;
        setError(errorMessage);

        // Check if it's a rate limit or temporary high demand error
        const isTemporaryError = data.error.statusCode === 429 || data.error.statusCode === 503;
        if (isTemporaryError && !isRetry) {
          setIsRetrying(true);
          // Auto-retry after a delay if user hasn't manually retried
          searchTimeoutRef.current = setTimeout(() => {
            if (retryCount < 2) {
              console.log('Auto-retrying after temporary AI service error...');
              setRetryCount(prev => prev + 1);
              handleSearch(details, true);
            }
          }, 3000);
        }

        // Only console.error if it's NOT an internal error
        if (!data.error.isInternal) {
          console.error('Cost analysis error:', errorMessage);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze parts. Please try again.';
      setError(errorMessage);
      console.error('Network error:', err);
      setIsRetrying(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleRetry = () => {
    if (vehicleDetails && !isSearching) {
      setRetryCount(prev => prev + 1);
      handleSearch(vehicleDetails, true);
    }
  };

  // generateMockMarketplaceResults removed in favor of real eBay integration

  const fetchEbayResults = async (query: string, recommendation?: Recommendation) => {
    try {
      const ebayRes = await fetch('/api/ebay/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });

      const ebayData = await ebayRes.json();
      if (ebayData.success) {
        const results: PartResult[] = ebayData.data.map((item: any) => ({
          id: item.id,
          title: item.title,
          partNumber: recommendation?.partNumber || 'N/A',
          price: item.price,
          condition: item.condition,
          seller: {
            name: item.seller.username,
            rating: 0,
            feedbackCount: item.seller.feedbackScore,
            topRated: item.seller.feedbackPercentage === '100.0',
          },
          shipping: 'Check listing for details',
          imageUrl: item.imageUrl,
          imageAlt: item.title,
          compatibility: recommendation?.compatibility || 'Verify with seller',
          listingUrl: item.listingUrl,
        }));

        setPartsResults(results);
        setFilteredResults(results);

        // If 0 results and query was specific, maybe try broader in future but for now just clear
        if (results.length === 0) {
          console.log('No eBay results found for query:', query);
        }
      } else {
        console.warn('eBay search failed:', ebayData.error?.message || 'Unknown error');
        setPartsResults([]);
        setFilteredResults([]);
      }
    } catch (ebayErr) {
      console.error('Failed to fetch eBay results:', ebayErr);
      setPartsResults([]);
      setFilteredResults([]);
    }
  };

  const handleKeywordClick = async (keyword: string) => {
    const section = document.getElementById('results-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }

    // Find matching recommendation if available to keep compatibility info
    const matchingRec = recommendations.find(r => r.keywords.includes(keyword));
    await fetchEbayResults(keyword, matchingRec);
  };

  const handleFilterChange = (filters: FilterState) => {
    let filtered = [...partsResults];

    if (filters.priceRange) {
      filtered = filtered.filter((part) => {
        const price = parseFloat(part.price.replace('$', ''));
        return price >= filters.priceRange[0] && price <= filters.priceRange[1];
      });
    }

    if (filters.condition.length > 0) {
      filtered = filtered.filter((part) => filters.condition.includes(part.condition));
    }

    if (filters.sellerRating > 0) {
      filtered = filtered.filter((part) => part.seller.rating >= filters.sellerRating);
    }

    if (filters.shipping.length > 0) {
      filtered = filtered.filter((part) => {
        if (filters.shipping.includes('Free Shipping')) {
          return part.shipping.toLowerCase().includes('free');
        }
        if (filters.shipping.includes('Expedited')) {
          return part.shipping.toLowerCase().includes('expedited') || part.shipping.toLowerCase().includes('2-day');
        }
        return true;
      });
    }

    setFilteredResults(filtered);
  };

  const handleRelatedPartClick = (partName: string) => {
    if (vehicleDetails) {
      handleSearch({
        ...vehicleDetails,
        partDescription: partName
      });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAmazonRedirect = () => {
    if (!vehicleDetails) return;
    
    // Affiliate ID provided by user
    const amazonTag = 'autofixiq-20';
    
    // Attempt to use the exact AI-identified OEM Part Number first for precise matching.
    // If not found, intelligently fallback to the generic make/model/part description.
    const searchTarget = recommendations.length > 0 && recommendations[0].partNumber !== 'N/A' 
      ? recommendations[0].partNumber 
      : `${vehicleDetails.make} ${vehicleDetails.model} ${vehicleDetails.partDescription}`.trim();
      
    const query = encodeURIComponent(searchTarget);
    window.open(`https://www.amazon.com/s?k=${query}&tag=${amazonTag}`, '_blank');
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-32 bg-muted rounded-xl" />
            <div className="h-96 bg-muted rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
              <Icon name="SparklesIcon" size={28} className="text-primary-foreground" />
            </div>
            <h1 className="font-heading text-4xl font-bold text-text-primary">
              AI-Powered OEM Parts Finder
            </h1>
          </div>
          <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
            Find authentic OEM parts for your vehicle with intelligent recommendations and marketplace integration
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <Icon name="ExclamationTriangleIcon" size={24} className="text-red-600 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div>
                  <p className="font-heading text-sm font-semibold text-red-900">Analysis Error</p>
                  <p className="font-body text-sm text-red-700">{error}</p>
                </div>

                <div className="space-y-2">
                  {isRetrying ? (
                    <div className="flex items-center space-x-2 text-sm text-red-700">
                      <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                      <span>Automatically retrying... (Attempt {retryCount + 1}/3)</span>
                    </div>
                  ) : (
                    <button
                      onClick={handleRetry}
                      disabled={isSearching}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Icon name="ArrowPathIcon" size={16} />
                      <span>Retry Request</span>
                    </button>
                  )}
                  <p className="text-xs text-red-600">
                    💡 Tip: If AI services are busy, clicking retry will attempt the analysis with an alternate model.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-card rounded-xl shadow-elevation-lg p-6 sm:p-8">
              <div className="flex items-center space-x-3 mb-6">
                <Icon name="TruckIcon" size={24} className="text-primary" />
                <h2 className="font-heading text-xl font-semibold text-text-primary">
                  Enter Vehicle Details
                </h2>
              </div>
              <VehicleInputForm onSearch={handleSearch} isLoading={isSearching} />
            </div>

            {isSearching && (
              <div className="bg-card rounded-xl shadow-elevation-md p-8">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <Icon name="ArrowPathIcon" size={48} className="text-primary animate-spin" />
                  <p className="font-heading text-lg font-semibold text-text-primary">
                    AI Analyzing Your Request...
                  </p>
                  <p className="font-body text-sm text-text-secondary text-center max-w-md">
                    Our AI is identifying the exact OEM part numbers and generating pricing insights for your {vehicleDetails?.year} {vehicleDetails?.make} {vehicleDetails?.model}
                  </p>
                </div>
              </div>
            )}

            {hasSearched && !isSearching && recommendations.length > 0 &&
              <AIRecommendations
                recommendations={recommendations}
                onKeywordClick={handleKeywordClick} />

            }

            {hasSearched && !isSearching && (
              <div id="results-section" className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-text-primary mb-1">
                      Marketplace Results
                    </h2>
                    <p className="font-body text-sm text-text-secondary">
                      {filteredResults.length > 0
                        ? `Found ${filteredResults.length} authentic OEM parts`
                        : 'No authentic OEM parts found for this query'}
                    </p>
                  </div>
                  
                  <button 
                    onClick={handleAmazonRedirect}
                    className="flex shrink-0 items-center justify-center space-x-2 rounded-lg bg-[#FF9900] px-4 py-2.5 font-body text-sm font-semibold text-black hover:bg-[#E38800] transition-smooth hover:-translate-y-0.5"
                  >
                    <Icon name="ShoppingCartIcon" size={18} />
                    <span>Check Prices on Amazon</span>
                  </button>
                </div>

                {partsResults.length > 0 && <FilterOptions onFilterChange={handleFilterChange} />}

                <div className="space-y-4">
                  {filteredResults.map((part) =>
                    <PartsResultCard key={part.id} part={part} />
                  )}
                </div>

                {filteredResults.length === 0 && (
                  <div className="bg-card rounded-xl shadow-elevation-md p-8 text-center">
                    <Icon name="FaceFrownIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">
                      No Results Found
                    </h3>
                    <p className="font-body text-sm text-text-secondary">
                      {partsResults.length === 0
                        ? "We couldn't find any matching parts on the marketplace. Try a different keyword or check your vehicle details."
                        : "Try adjusting your filters to see more results."}
                    </p>
                  </div>
                )}
              </div>
            )}

            {hasSearched && !isSearching && vehicleDetails &&
              <RelatedParts
                vehicleYear={vehicleDetails.year}
                vehicleMileage={vehicleDetails.mileage}
                onPartClick={handleRelatedPartClick} />

            }
          </div>

          <div className="space-y-8">
            <SafetyWarnings />

            <div className="bg-card rounded-xl shadow-elevation-md p-6 space-y-4">
              <div className="flex items-center space-x-2">
                <Icon name="QuestionMarkCircleIcon" size={24} className="text-primary" />
                <h3 className="font-heading text-lg font-semibold text-text-primary">
                  How It Works
                </h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-heading text-sm font-semibold flex-shrink-0">
                    1
                  </div>
                  <div className="flex-1">
                    <p className="font-heading text-sm font-semibold text-text-primary mb-1">
                      Enter Vehicle Info
                    </p>
                    <p className="font-body text-xs text-text-secondary">
                      Provide your vehicle details and describe the part you need
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-heading text-sm font-semibold flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <p className="font-heading text-sm font-semibold text-text-primary mb-1">
                      AI Analysis
                    </p>
                    <p className="font-body text-xs text-text-secondary">
                      Our AI suggests exact part numbers and search keywords
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-heading text-sm font-semibold flex-shrink-0">
                    3
                  </div>
                  <div className="flex-1">
                    <p className="font-heading text-sm font-semibold text-text-primary mb-1">
                      Browse Results
                    </p>
                    <p className="font-body text-xs text-text-secondary">
                      Review authentic OEM parts from trusted sellers
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-heading text-sm font-semibold flex-shrink-0">
                    4
                  </div>
                  <div className="flex-1">
                    <p className="font-heading text-sm font-semibold text-text-primary mb-1">
                      Purchase Safely
                    </p>
                    <p className="font-body text-xs text-text-secondary">
                      Buy with confidence from verified sellers
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl border border-primary/20 p-6 space-y-3">
              <div className="flex items-center space-x-2">
                <Icon name="ShieldCheckIcon" size={24} className="text-primary" />
                <h3 className="font-heading text-base font-semibold text-text-primary">
                  Why Use Our Finder?
                </h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="font-body text-sm text-text-secondary">
                    AI-powered part number identification
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="font-body text-sm text-text-secondary">
                    Real-time marketplace integration
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="font-body text-sm text-text-secondary">
                    Verified seller ratings and reviews
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="font-body text-sm text-text-secondary">
                    Compatibility verification included
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OEMPartsFinderInteractive;